import { useCallback, useMemo, useState } from 'react';
import { uniq } from 'underscore';

import { useAppSelector } from '@/store/hooks';
import { TReactSelectOption } from '@ui/select/react-select/custom-react-select.types';
import { selectAllProjectCategoriesForChannel } from '@/store/slices/categories/selectors';
import useLoadChannelProjectCategories from '../use-load-channel-project-categories';
import EditStateSelect from '@ui/edit-state-wrapper/edit-state-select';
import preventMultiValueArrowNav from '@ui/edit-state-wrapper/prevent-multi-value-arrow-nav';

type TCategoryOption = TReactSelectOption<string, string, string>;

const cleanCategoriesList = (defaultCategories: string[] | null, addedCategories?: TReactSelectOption[]) =>
  uniq([...(defaultCategories || []), ...(addedCategories || []).map((category) => String(category.value))]).sort(
    (a, b) => (a.toLowerCase() > b.toLowerCase() ? 1 : -1)
  );

type ProjectCategoriesSelectProps = {
  defaultCategories?: TReactSelectOption[];
  onMenuClose: (selectedCategories: TCategoryOption[]) => void;
  disabled?: boolean;
};

const ProjectCategoriesSelect = ({
  defaultCategories,
  onMenuClose,
  disabled = false,
}: ProjectCategoriesSelectProps) => {
  useLoadChannelProjectCategories({ skipIfCategoriesExist: true });

  const allProjectCategoriesForChannel = useAppSelector(selectAllProjectCategoriesForChannel);

  const [displayCategories, setDisplayCategories] = useState<TReactSelectOption[]>(defaultCategories || []);

  const allProjectCategories = useMemo(() => {
    // allProjectCategoriesForChannel only returns categories that are a part of templates.
    // We need to also include categories assigned to this project
    if (!defaultCategories) return allProjectCategoriesForChannel;
    return cleanCategoriesList(allProjectCategoriesForChannel, defaultCategories);
  }, [allProjectCategoriesForChannel, defaultCategories]);

  const options = useMemo(() => {
    if (!allProjectCategories) return [];
    return cleanCategoriesList(allProjectCategories, displayCategories).map((c) => ({
      label: c.toString(),
      value: c.toString(),
      meta: c.toString(),
    }));
  }, [allProjectCategories, displayCategories]);

  const handleMenuClose = useCallback(
    (selectedOptions: TReactSelectOption[]) => {
      const _selectedOptions = selectedOptions.map((option) => ({
        label: option.label.toString(),
        value: option.value.toString(),
        meta: (option.meta || option.value).toString(),
      }));

      onMenuClose(_selectedOptions as TCategoryOption[]);

      setDisplayCategories(_selectedOptions);
    },
    [onMenuClose]
  );

  return (
    <EditStateSelect
      Label="CATEGORIES"
      placeholder="Add category"
      defaultValue={defaultCategories}
      options={options}
      onChange={setDisplayCategories}
      onMenuClose={() => handleMenuClose(displayCategories)}
      disabled={disabled}
      isCreatable
      onKeyDown={preventMultiValueArrowNav}
    />
  );
};

export default ProjectCategoriesSelect;
